/**
 * Shared UI primitives for the Analysis Suite (Deal Underwriter & Project Analysis).
 *
 * Both pages were redefining identical components — extracting them here
 * means future polish (label tweaks, tooltip support, etc.) auto-applies
 * everywhere and we stop drifting visually. Pure presentation, zero logic.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// ---- formatters ----
export const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
export const fmt2 = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
export const fmtPct = (n) => (n == null ? '—' : `${(Number(n) || 0).toFixed(2)}%`);

/**
 * NumInput — labelled numeric input with optional $ prefix or % / unit suffix.
 * Set `prefix={null}` to suppress the dollar sign for non-currency fields.
 */
export const NumInput = ({ label, value, onChange, prefix = '$', suffix, hint, testid, placeholder }) => (
  <div>
    <Label className="text-xs font-medium text-slate-700">{label}</Label>
    <div className="relative mt-1">
      {prefix && (
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{prefix}</span>
      )}
      <Input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        onFocus={(e) => e.target.select()}
        placeholder={placeholder}
        className={`h-8 text-sm font-mono ${prefix ? 'pl-6' : ''} ${suffix ? 'pr-8' : ''}`}
        data-testid={testid}
      />
      {suffix && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{suffix}</span>
      )}
    </div>
    {hint && <p className="text-[10px] text-slate-500 mt-0.5">{hint}</p>}
  </div>
);

/**
 * Section — collapsible card with icon, title, optional subtitle.
 */
export const Section = ({ title, icon: Icon, subtitle, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="p-4 mb-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between mb-2"
      >
        <div className="text-left">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-blue-600" />}
            {title}
          </h3>
          {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {open
          ? <ChevronUp   className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />}
      </button>
      {open && <div className="space-y-3">{children}</div>}
    </Card>
  );
};

/**
 * KpiTile — labelled stat with color semantics.
 *   color: 'emerald' (positive) | 'red' (loss) | 'blue' (neutral) | 'amber' (caution) | 'slate' (info)
 */
export const KpiTile = ({ label, value, color = 'slate', sub }) => {
  const palette = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    red:     'bg-red-50 border-red-200 text-red-900',
    blue:    'bg-blue-50 border-blue-200 text-blue-900',
    slate:   'bg-slate-50 border-slate-200 text-slate-900',
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
  }[color];
  return (
    <div className={`border rounded-lg p-3 ${palette}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
      <p className="text-xl font-bold font-mono mt-0.5">{value}</p>
      {sub && <p className="text-[10px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
};
